// src/modules/app-views/crm/pages/Kanban/modals/NewOpportunityFormModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigProvider } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import useZustand from 'hooks/useZustand';
import { darkTheme, lightTheme } from 'configs/appEngine';
import axiosService from 'services/axiosService';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { API } from 'constants/Api';
import Step1 from './Step1';

// *******************************************************************************
// THEME WRAPPER
// *******************************************************************************
const ThemeWrapper = ({ children }) => {
  const theme = useZustand('theme', 'currentTheme');
  const config = theme === 'dark' ? darkTheme : lightTheme;
  return <ConfigProvider theme={config}>{children}</ConfigProvider>;
};

// *******************************************************************************
// MAIN MODAL COMPONENT
// *******************************************************************************
const NewOpportunityFormModal = ({ show, setShow, afterClose, boardUid, columnUid, onSuccess }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const boards = storedParameters?.modules?.crm?.boards || [];
  const selectedBoard = boards.find(b => b.uid === boardUid);
  const sortedColumns = [...(selectedBoard?.columns || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const initialColumn = sortedColumns.find(c => c.initial) || sortedColumns[0] || null;
  const initialColumnUid = initialColumn?.uid || columnUid || null;
  const boardFormFields = selectedBoard?.formFields || [];
  const opportunityFieldKeys = selectedBoard?.opportunityField || [];
  const intrinsicFieldLabels = selectedBoard?.intrinsicFieldLabels || {};

  // Build validationFields: basic required + dynamic custom required fields
  const requiredFormFields = boardFormFields
    .filter(f => (f.requiredInColumns || []).includes(initialColumnUid))
    .map(f => ['formData', f.fieldName]);

  const hasOpportunityComposition = opportunityFieldKeys.length > 0;

  const validationFields = [
    'name',
    ['phone', 'value'],
    ['email', 'value'],
    'consultantId',
    // Manual opportunity field only when no composition is configured
    ...(hasOpportunityComposition ? [] : ['opportunity']),
    ...requiredFormFields,
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    const values = form?.getFieldsValue(true) || {};

    // Phone OR email — at least one required
    const phone = values?.phone?.value?.trim() || '';
    const email = values?.email?.value?.trim() || '';
    if (!phone && !email) {
      appService.message({ flag: 'w', text: 'phone_or_email_required' });
      return false;
    }

    // 1. Create person entity (profile: opportunity)
    const personPayload = buildPersonPayload(values);
    const [createdPerson] = await axiosService.post({
      url: API.ENTITIES.URL.replace(':_id?', ''),
      data: personPayload,
      returnAsObject: true,
    });
    const entityId = createdPerson?._id || null;

    // 2. Create CRM opportunity
    // Compose opportunity from selected custom fields, or use manual input as fallback
    const opportunity =
      opportunityFieldKeys.length > 0
        ? composeOpportunity(values.formData, opportunityFieldKeys)
        : (values.opportunity || '').trim();

    const consultantId = values.consultantId || undefined;

    const userUniqueKey = (values.uniqueKey || '').trim();

    const crmPayload = {
      status: 'backlog',
      opportunityType: 'sale',
      opportunity: opportunity || 'n/a',
      uniqueKey: userUniqueKey || undefined,
      boardUid,
      columnUid: initialColumnUid,
      entityId,
      personContactId: entityId,
      ...(consultantId ? { consultantId, consultantIds: [consultantId] } : {}),
      ...(values.formData ? { formData: values.formData } : {}),
    };

    const [createdCrm] = await axiosService.post({
      url: API.CRM.URL.replace(':_id?', ''),
      data: crmPayload,
      returnAsObject: true,
    });

    // Auto-generate uniqueKey from MongoDB _id when user left it empty
    if (!userUniqueKey && createdCrm?._id) {
      await axiosService.put({
        url: API.CRM.URL.replace(':_id?', createdCrm._id),
        data: { uniqueKey: createdCrm._id },
        returnAsObject: true,
      });
    }

    // 3. Refresh kanban board
    window.dispatchEvent(new CustomEvent('crm:kanban:refresh'));

    appService.message({ flag: 's', text: 'saved_successfully' });
    onSuccess?.();
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [
      {
        stepTitle: 'new_opportunity',
        validationFields,
        onEnter: ({ form }) => {
          setTimeout(() => form?.getFieldInstance?.('name')?.focus?.(), 150);
        },
        content: () => (
          <Step1
            formFields={boardFormFields}
            columnUid={initialColumnUid}
            opportunityField={opportunityFieldKeys}
            intrinsicFieldLabels={intrinsicFieldLabels}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boardFormFields, initialColumnUid]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      title={translateX('new_opportunity')}
      icon={<UserAddOutlined />}
      steps={steps}
      onFinish={handleFinish}
      initialValues={{ boardUid }}
      width={540}
    />
  );
};

// *******************************************************************************
// EXPORT (named — for reuse as React component)
// *******************************************************************************
export { NewOpportunityFormModal };

// *******************************************************************************
// IMPERATIVE SHOW FUNCTION (default export)
// *******************************************************************************
function showNewOpportunityFormModal({ boardUid, columnUid, onSuccess } = {}) {
  const container = document.createElement('div');
  container.id = 'new-opportunity-form-modal-container';
  document.body.appendChild(container);

  const root = createRoot(container);

  let isModalOpen = true;

  const renderModal = () => {
    root.render(
      <ThemeWrapper>
        <NewOpportunityFormModal
          show={isModalOpen}
          setShow={handleClose}
          afterClose={handleAfterClose}
          boardUid={boardUid}
          columnUid={columnUid}
          onSuccess={onSuccess}
        />
      </ThemeWrapper>
    );
  };

  const handleClose = () => {
    isModalOpen = false;
    renderModal();
  };

  const handleAfterClose = () => {
    setTimeout(() => {
      root.unmount();
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }, 0);
  };

  renderModal();
}

export default showNewOpportunityFormModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildPersonPayload(values) {
  const payload = {
    name: (values.name || '').trim(),
    entityType: 'person',
    entityProfile: ['opportunity'],
  };

  if (values.phone?.value?.trim()) {
    payload.phone = {
      value: values.phone.value.trim(),
      countryCode: values.phone.countryCode || 'BR',
      type: values.phone.type || 'mobile',
    };
  }

  if (values.email?.value?.trim()) {
    payload.email = {
      value: values.email.value.trim(),
      countryCode: 'BR',
    };
  }

  return payload;
}

// Compose the opportunity description by concatenating selected custom field values
// in the order defined by opportunityFieldKeys.
function composeOpportunity(formData, fieldKeys) {
  if (!formData || !fieldKeys?.length) return '';
  return fieldKeys
    .map(key => {
      const val = formData[key];
      if (val == null) return '';
      return String(val).trim();
    })
    .filter(Boolean)
    .join(' ');
    // .join(' | ');
}
